// Vendored from mdast-util-gfm-autolink-literal@2.0.1 with one change: the
// email pattern no longer uses a lookbehind assertion, which Safari < 16.4
// cannot parse ("Invalid regular expression: invalid group specifier name").
// The lookbehind was redundant: findEmail already rejects matches not preceded
// by start/whitespace/punctuation via `previous`. Wired in through the vite
// alias in vite.config.ts.

import { ccount } from "ccount";
import { ok as assert } from "devlop";
import type { Link, PhrasingContent, Root } from "mdast";
import type {
  CompileContext,
  Extension as FromMarkdownExtension,
  Token,
} from "mdast-util-from-markdown";
import {
  unicodePunctuation,
  unicodeWhitespace,
} from "micromark-util-character";
import {
  findAndReplace,
  type RegExpMatchObject,
  type ReplaceFunction,
} from "mdast-util-find-and-replace";
import type {
  ConstructName,
  Options as ToMarkdownExtension,
} from "mdast-util-to-markdown";

export const safeGfmAutolinkLiteralModuleName =
  "mdast-util-gfm-autolink-literal";

const inConstruct: ConstructName = "phrasing";
const notInConstruct: ConstructName[] = ["autolink", "link", "image", "label"];

export const gfmAutolinkLiteralFromMarkdown = (): FromMarkdownExtension => ({
  transforms: [transformGfmAutolinkLiterals],
  enter: {
    literalAutolink: enterLiteralAutolink,
    literalAutolinkEmail: enterLiteralAutolinkValue,
    literalAutolinkHttp: enterLiteralAutolinkValue,
    literalAutolinkWww: enterLiteralAutolinkValue,
  },
  exit: {
    literalAutolink: exitLiteralAutolink,
    literalAutolinkEmail: exitLiteralAutolinkEmail,
    literalAutolinkHttp: exitLiteralAutolinkHttp,
    literalAutolinkWww: exitLiteralAutolinkWww,
  },
});

export const gfmAutolinkLiteralToMarkdown = (): ToMarkdownExtension => ({
  unsafe: [
    {
      character: "@",
      before: "[+\\-.\\w]",
      after: "[\\-.\\w]",
      inConstruct,
      notInConstruct,
    },
    {
      character: ".",
      before: "[Ww]",
      after: "[\\-.\\w]",
      inConstruct,
      notInConstruct,
    },
    {
      character: ":",
      before: "[ps]",
      after: "\\/",
      inConstruct,
      notInConstruct,
    },
  ],
});

function enterLiteralAutolink(this: CompileContext, token: Token) {
  this.enter({ type: "link", title: null, url: "", children: [] }, token);
}

function enterLiteralAutolinkValue(this: CompileContext, token: Token) {
  this.config.enter.autolinkProtocol.call(this, token);
}

function exitLiteralAutolinkHttp(this: CompileContext, token: Token) {
  this.config.exit.autolinkProtocol.call(this, token);
}

function exitLiteralAutolinkWww(this: CompileContext, token: Token) {
  this.config.exit.data.call(this, token);
  const node = this.stack[this.stack.length - 1];
  assert(node.type === "link");
  node.url = "http://" + this.sliceSerialize(token);
}

function exitLiteralAutolinkEmail(this: CompileContext, token: Token) {
  this.config.exit.autolinkEmail.call(this, token);
}

function exitLiteralAutolink(this: CompileContext, token: Token) {
  this.exit(token);
}

const transformGfmAutolinkLiterals = (tree: Root) => {
  findAndReplace(
    tree,
    [
      [/(https?:\/\/|www(?=\.))([-.\w]+)([^ \t\r\n]*)/gi, findUrl],
      [/([-.\w+]+)@([-\w]+(?:\.[-\w]+)+)/gu, findEmail],
    ],
    { ignore: ["link", "linkReference"] },
  );
};

const findUrl: ReplaceFunction = (
  _: string,
  protocol: string,
  domain: string,
  path: string,
  match: RegExpMatchObject,
): PhrasingContent[] | Link | false => {
  let prefix = "";

  if (!previous(match)) {
    return false;
  }

  if (/^w/i.test(protocol)) {
    domain = protocol + domain;
    protocol = "";
    prefix = "http://";
  }

  if (!isCorrectDomain(domain)) {
    return false;
  }

  const parts = splitUrl(domain + path);

  if (!parts[0]) return false;

  const result: Link = {
    type: "link",
    title: null,
    url: prefix + protocol + parts[0],
    children: [{ type: "text", value: protocol + parts[0] }],
  };

  if (parts[1]) {
    return [result, { type: "text", value: parts[1] }];
  }

  return result;
};

const findEmail: ReplaceFunction = (
  _: string,
  atext: string,
  label: string,
  match: RegExpMatchObject,
): Link | false => {
  if (
    !previous(match, true) ||
    /[-\d_]$/.test(label)
  ) {
    return false;
  }

  return {
    type: "link",
    title: null,
    url: "mailto:" + atext + "@" + label,
    children: [{ type: "text", value: atext + "@" + label }],
  };
};

const isCorrectDomain = (domain: string): boolean => {
  const parts = domain.split(".");

  if (
    parts.length < 2 ||
    (parts[parts.length - 1] &&
      (/_/.test(parts[parts.length - 1]) ||
        !/[a-zA-Z\d]/.test(parts[parts.length - 1]))) ||
    (parts[parts.length - 2] &&
      (/_/.test(parts[parts.length - 2]) ||
        !/[a-zA-Z\d]/.test(parts[parts.length - 2])))
  ) {
    return false;
  }

  return true;
};

const splitUrl = (url: string): [string, string | undefined] => {
  const trailExec = /[!"&'),.:;<>?\]}]+$/.exec(url);

  if (!trailExec) {
    return [url, undefined];
  }

  url = url.slice(0, trailExec.index);

  let trail = trailExec[0];
  let closingParenIndex = trail.indexOf(")");
  const openingParens = ccount(url, "(");
  let closingParens = ccount(url, ")");

  while (closingParenIndex !== -1 && openingParens > closingParens) {
    url += trail.slice(0, closingParenIndex + 1);
    trail = trail.slice(closingParenIndex + 1);
    closingParenIndex = trail.indexOf(")");
    closingParens++;
  }

  return [url, trail];
};

const previous = (match: RegExpMatchObject, email?: boolean): boolean => {
  const code = match.input.charCodeAt(match.index - 1);

  return (
    (match.index === 0 ||
      unicodeWhitespace(code) ||
      unicodePunctuation(code)) &&
    (!email || code !== 47)
  );
};
